/**
 * Singleton decorator that ensures only one instance of a method's return value is created
 * and cached for subsequent calls.
 * 
 * Usage:
 * @singleton()
 * getMyService() {
 *     return new MyService();
 * }
 */
export function singleton() {
    return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
        const originalMethod = descriptor.value;
        const cache = new Map();
        
        descriptor.value = function (...args: any[]) {
            // Create a cache key based on the method name and arguments
            const cacheKey = `${propertyKey}_${JSON.stringify(args)}`;
            
            // If we already have a cached instance, return it
            if (cache.has(cacheKey)) {
                return cache.get(cacheKey);
            }
            
            // Otherwise, call the original method and cache the result
            const result = originalMethod.apply(this, args);
            cache.set(cacheKey, result);
            
            return result;
        };
        
        return descriptor;
    };
}

export default singleton;
